import { FC } from "react";

import CollapsiblePanel from "components/CollapsiblePanel";
import CollapsiblePanelContent from "components/CollapsiblePanel/Content";
import CollapsiblePanelToggleTrigger from "components/CollapsiblePanel/ToggleTrigger";
import { TableContext } from "components/Table/Context";
import Box from "ds/components/Box";
import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import tableBodyStyles from "ds/components/Table/Body/styles.module.css";
import TableBodyRow from "ds/components/Table/Row";
import TableCell from "ds/components/Table/Row/Cell";
import TextEllipsis from "ds/components/TextEllipsis";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElementWithSource } from "types/generated";

import cellStyles from "../../../Account/IacManagement/components/Table/Row/styles.module.css";
import { FilesColumnId } from "../constants";
import DescriptionCell from "./Cells/DescriptionCell";
import NameCell from "./Cells/NameCell";
import SecretCell from "./Cells/SecretCell";
import SourceCell from "./Cells/SourceCell";
import TimestampCell from "./Cells/TimestampCell";
import { VariableCellComponentProps } from "./Cells/types";

type FilesRowProps = {
  config: ConfigElementWithSource;
  isAdmin?: boolean;
  actionsComponent: FC<VariableCellComponentProps>;
};

const CELLS = {
  [FilesColumnId.EXPAND]: () => (
    <CollapsiblePanelToggleTrigger ariaLevel={3} ariaLabel="File toggle" />
  ),
  [FilesColumnId.NAME]: NameCell,
  [FilesColumnId.DESCRIPTION]: DescriptionCell,
  [FilesColumnId.SECRET]: SecretCell,
  [FilesColumnId.SOURCE]: SourceCell,
  [FilesColumnId.CREATED_AT]: TimestampCell,
  [FilesColumnId.ACTIONS]: undefined,
};

const FilesRow = ({ config, isAdmin = false, actionsComponent }: FilesRowProps) => {
  const rowId = config.element.id;
  const [isCollapsed, toggle] = useToggle(true);
  const { visibleColumnIds } = useTypedContext(TableContext);
  const isActive = useDrawerVisibilityForId(rowId);

  return (
    <CollapsiblePanel
      className={tableBodyStyles.tableBody}
      role="rowgroup"
      isCollapsed={isCollapsed}
      onToggle={toggle}
    >
      <TableBodyRow isActive={isActive}>
        {visibleColumnIds.map((id) => {
          const Component = CELLS[id as FilesColumnId] ?? actionsComponent;
          const uniqueKey = `${id}-${config.element.id}`;
          return (
            <TableCell key={uniqueKey} id={id}>
              <Component variable={config.element} context={config.context} isAdmin={isAdmin} />
            </TableCell>
          );
        })}
      </TableBodyRow>
      <CollapsiblePanelContent className={cellStyles.row} role="row">
        <TableCell aria-colspan={visibleColumnIds.length}>
          <Box>
            <TextEllipsis tooltip={config.element.value ?? ""} tooltipWidthMode="maxWidthSm">
              {(props) => <span {...props}>{config.element.value ?? ""}</span>}
            </TextEllipsis>
          </Box>
        </TableCell>
      </CollapsiblePanelContent>
    </CollapsiblePanel>
  );
};

export default FilesRow;
